<template>
	<Modal
		:theme="$arenaConfig.isLightTheme ? null : 'dark'"
		class="payment__modal"
		modal-name="PaymentModal"
		:closeable="closeable"
		data-testid="payment-modal"
		@opened="onOpen"
	>
		<template #title>
			{{ $I18n.trans('payments.modal.title_' + mode ) }}
		</template>
		<template #content>
			<div class="l-container payment-modal__content">
				<div
					v-if="steps.length > 1"
					class="l-row l-align-center"
				>
					<StepsIndicator
						class="payment-modal__steps"
						arena-theme
						:steps="steps"
						:active-step="activeStep"
					/>
				</div>
				<template v-if="isLogin">
					<div
						v-if="!hideIntro"
						class="l-row l-align-center"
					>
						<div class="l-col l-col--12">
							<div class="payment-modal__head">
								<h2
									class="payment-modal__title"
									data-testid="payment-modal-title"
								>
									{{ $I18n.trans('payments.modal.step_1.headline_' + mode) }}
								</h2>
								<span class="payment-modal__subtitle">{{ $I18n.trans('payments.modal.step_1.subheadline') }}</span>
							</div>
						</div>
					</div>
					<LoginComponent
						:login-redirect-url="loginRedirectUrl"
						@submitted="loginSubmitted"
					/>
				</template>
				<template v-if="isCheckout">
					<div class="l-row l-align-center">
						<div class="l-col l-col--12">
							<div
								v-if="!voucherValid && availableOffers && availableOffers.length > 0"
								class="payment-modal__head"
							>
								<h2
									class="payment-modal__title"
									data-testid="payment-modal-title"
								>
									{{ $I18n.trans(`payments.modal.step_2.${showGrupedOffers ? 'headline_group' : 'headline'}`) }}
								</h2>
								<span class="payment-modal__subtitle">{{ $I18n.trans(`payments.modal.step_2.${showGrupedOffers ? 'subheadline_group' : 'subheadline'}`) }}</span>
							</div>
							<div
								v-if="voucherValid"
								class="payment-modal__head"
							>
								<h2
									class="payment-modal__title"
									data-testid="payment-modal-title"
								>
									{{ $I18n.trans('payments.modal.voucher.headline') }}
								</h2>
								<span class="payment-modal__subtitle">{{ $I18n.trans('payments.modal.voucher.subheadline') }}</span>
							</div>
							<div
								v-if="showGrupedOffers"
								class="payment-modal__grouped-offers-container"
							>
								<div class="payment-modal__grouped-offers">
									<PaymentGroupCard
										v-for="groupedOffer in groupedOffers"
										:key="groupedOffer.type"
										:title="groupedOffer.title"
										:info="groupedOffer.info"
										:min-price="groupedOffer.minPriceFormatted"
										:currency-code="groupedOffer.currencyCode"
										:features="groupedOffer.features"
										:selected="selectedGroupOfferType === groupedOffer.type"
										:light-theme="$arenaConfig.isLightTheme"
										@select="selectedGroupOfferType = groupedOffer.type"
									/>
								</div>
								<Button
									class="payment-modal__grouped-offers-button"
									:text="$I18n.trans('commons.continue')"
									:theme="$arenaConfig.isLightTheme ? null : 'dark'"
									:disabled="!selectedGroupOfferType"
									@click="handleGroupedOfferSelect"
								/>
							</div>
							<div v-else-if="filteredAvailableOffers && filteredAvailableOffers.length > 0">
								<div class="payment-modal__offer-cards-w">
									<PaymentOfferCard
										v-for="paymentOffer in filteredAvailableOffers"
										:key="paymentOffer.id"
										:title="getPaymentOfferName(paymentOffer)"
										:description="paymentOffer.description ? $Helpers.getTranslatedContent(paymentOffer.description) : null"
										:info="event && paymentOffer.type === PaymentOfferType.PPV ? getDateTime(event.startTimestamp) : null"
										:img-url="paymentOffer.imageUrl"
										:price="(paymentOffer.amount / 100).toFixed(2)"
										:currency-code="paymentOffer.currencyCode"
										:validity-info="paymentOffer.type === PaymentOfferType.SUBSCRIPTION ? $I18n.trans('payments.validity.' + paymentOffer.validity) : null"
										:is-selected="paymentOffer.id === offer.id"
										:has-multiple="filteredAvailableOffers.length > 1"
										:light-theme="$arenaConfig.isLightTheme"
										@select="setOffer(paymentOffer)"
									/>
								</div>
								<!-- eslint-disable vue/no-v-html -->
								<div
									v-if="$arenaConfig.isSfl"
									class="u-mb-5 s-html -sm"
									v-html="$I18n.trans('payments.modal.subscription.disclaimer2')"
								/>
								<!--eslint-enable-->
								<div v-if="offer.type === PaymentOfferType.SUBSCRIPTION">
									<InputCheckbox
										id="confirm-subscription"
										v-model="confirmSubscription"
										required="true"
										:dark-theme="!$arenaConfig.isLightTheme"
										:label="$I18n.trans('payments.modal.subscription.disclaimer')"
									/>
								</div>
								<div
									v-if="offer.voucherEnabled && !voucherValid"
									class="voucher"
								>
									<InputLabel
										class="voucher__label"
										:text="$I18n.trans('payments.modal.voucher.use_voucher')"
									/>
									<div class="voucher__input-container">
										<InputText
											class="voucher__input"
											name="voucher"
											type="text"
											:placeholder="$I18n.trans('payments.modal.voucher.placehodler')"
											:dark-theme="!$arenaConfig.isLightTheme"
											:value="voucherCode"
											:custom-error-msg="$I18n.trans('payments.modal.voucher.invalid')"
											:show-error="voucherChecked && !voucherValid"
											@input="(data) => voucherCode = data"
										/>
										<Button
											class="voucher__apply"
											:text="$I18n.trans('commons.check')"
											modifier="secondary"
											:disabled="isBusy || !voucherCode"
											:theme="$arenaConfig.isLightTheme ? null : 'dark'"
											@click="checkVoucher"
										/>
									</div>
								</div>
								<div class="l-button-group">
									<Button
										v-if="selectedGroupOfferConfirmed"
										class="payment-modal__continue"
										:text="$Helpers.ucfirst($I18n.trans('commons.back'))"
										:disabled="isBusy"
										modifier="secondary"
										:theme="$arenaConfig.isLightTheme ? null : 'dark'"
										data-testid="payment-modal-back"
										@click="selectedGroupOfferConfirmed = false"
									/>
									<Button
										v-if="offer"
										class="payment-modal__continue"
										:text="submitText"
										:disabled="isBusy || !canContinue"
										:theme="$arenaConfig.isLightTheme ? null : 'dark'"
										data-testid="payment-modal-continue"
										@click="submitForm"
									/>
								</div>
								<InputError
									v-if="error"
									class="payment-modal__error"
									:text="$I18n.trans('payments.modal.step_2.error')"
								/>
							</div>
							<div
								v-if="availableOffers && availableOffers.length === 0"
								class="payment-modal__offer -center"
							>
								{{ $I18n.trans('payments.paywall.no_offers_' + mode) }}
							</div>
						</div>
					</div>
				</template>
				<template v-if="isConfirm">
					<template v-if="isPaymentPending">
						<div class="payment-modal__head">
							<h2
								class="payment-modal__title"
								data-testid="payment-modal-title"
							>
								{{ $I18n.trans('payments.modal.step_3.please_wait.title') }}
							</h2>
							<span class="payment-modal__subtitle">{{ $I18n.trans('payments.modal.step_3.please_wait.subtitle') }}</span>
							<LoadingSpinner
								class="paywall-modal__loading"
							/>
							<!-- eslint-disable vue/no-v-html -->
							<span
								class="payment-modal__subtitle"
								v-html="$I18n.trans('payments.modal.step_3.please_wait.reference', { orderCode: urlParams.get('orderCode') } )"
							/>
							<!--eslint-enable-->
						</div>
					</template>
					<template v-if="isPaymentConfirmed && purchasedOffer">
						<div class="payment-modal__head">
							<icon
								icon-name="fireworks"
								size="xxxl"
								class="payment-modal__icon"
							/>
							<h2
								class="payment-modal__title"
								data-testid="payment-modal-title"
							>
								{{ $I18n.trans('payments.modal.step_3.confirmed.title') }}
							</h2>
							<div>
								<!-- eslint-disable vue/no-v-html -->
								<span
									v-if="purchasedOffer.type === 'ppv'"
									class="payment-modal__subtitle"
									v-html="$I18n.trans('payments.modal.step_3.confirmed.subtitle_event', { event: event.name })"
								/>
								<span
									v-else
									class="payment-modal__subtitle"
									v-html="$I18n.trans('payments.modal.step_3.confirmed.subtitle_' + purchasedOffer.type, { purchase: $Helpers.getTranslatedContent(purchasedOffer.name) })"
								/>
								<div v-if="continueOnOtherDevice">
									{{ $I18n.trans('payments.modal.step_3.confirmed.continue_on_other_device') }}
								</div>
								<!--eslint-enable-->
								<Button
									v-if="event"
									class="payment-modal__continue"
									:text="$I18n.trans('payments.modal.step_3.confirmed.continue')"
									:theme="$arenaConfig.isLightTheme ? null : 'dark'"
									data-testid="payment-modal-continue"
									@click="reload()"
								/>
								<Button
									v-else
									class="payment-modal__continue"
									:text="$I18n.trans('commons.continue')"
									:theme="$arenaConfig.isLightTheme ? null : 'dark'"
									data-testid="payment-modal-continue"
									@click="toProfile()"
								/>
							</div>
						</div>
					</template>
					<template v-if="isPaymentFailed">
						<h2
							class="payment-modal__title -center"
							data-testid="payment-modal-title"
						>
							{{ $I18n.trans('payments.modal.error.title') }}
						</h2>

						<icon
							icon-name="sad"
							size="xxxl"
							class="payment-modal__icon"
						/>
						<span
							class="payment-modal__subtitle -center"
							v-html="$I18n.trans('payments.modal.error.subtitle', { code: urlParams.get('orderCode') })"
						/>
						<Button
							class="payment-modal__continue"
							:text="$I18n.trans('payments.modal.error.back')"
							:theme="$arenaConfig.isLightTheme ? null : 'dark'"
							data-testid="payment-modal-back"
							@click="reload()"
						/>
					</template>
					<template v-if="isPaymentCanceled">
						<h2
							class="payment-modal__title -center"
							data-testid="payment-modal-title"
						>
							{{ $I18n.trans('payments.modal.canceled.title') }}
						</h2>

						<icon
							icon-name="sad"
							size="xxxl"
							class="payment-modal__icon"
						/>
						<span
							class="payment-modal__subtitle -center"
							v-html="$I18n.trans('payments.modal.canceled.subtitle')"
						/>
						<Button
							class="payment-modal__continue"
							:text="$I18n.trans('payments.modal.canceled.back')"
							:theme="$arenaConfig.isLightTheme ? null : 'dark'"
							data-testid="payment-modal-back"
							@click="reload()"
						/>
					</template>
				</template>
			</div>
		</template>
	</Modal>
</template>

<script>
import Modal from '@shared/components/Modal';
import LoginComponent from '@js/components/Login/LoginComponent';
import { isMatch } from '@shared/utils/eventUtils';
import StepsIndicator from '@shared/components/StepsIndicator';
import InputText from '@shared/components/Form/InputText';
import InputLabel from '@shared/components/Form/InputLabel';
import Button from '@shared/components/Button';
import InputError from '@shared/components/Form/InputError';
import Icon from '@shared/components/Icon';
import LoadingSpinner from '@shared/components/V2LoadingSpinner';
import LoggingService from '@shared/js/LoggingService';
import InputCheckbox from '@shared/components/Form/InputCheckbox';
import PaymentGroupCard from '@js/components/Payments/PaymentGroupCard.vue';
import PaymentOfferCard from '@js/components/Payments/PaymentOfferCard.vue';

import { formatCurrency } from '@shared/utils/formatNumber';
import { getDateTime } from '@shared/utils/timeUtils';
import { getGroupedOffers } from '@js/components/Payments/getGroupedOffers';

import storage from '@shared/js/storage';
import { authApi } from '@js/api/client';
import { PaymentOfferType } from '@shared/enums/PaymentOfferType';
import { httpClient } from '@shared/js/api/httpClient';
import { mapGetters, mapState } from 'vuex';
import laroute from '@laroute';

export const PAYMENT_STATE = {
	FAILED:   'paymentFailed',
	SUCCESS:  'paymentSuccess',
	CANCELED: 'paymentCanceled',
	PENDING:  'paymentPending',
};

export const ORDER_STATE = {
	PENDING:   'pending',
	CONFIRMED: 'confirmed',
	CREATED:   'created',
};

export const MODAL_STEPS = {
	login:    0,
	checkout: 1,
	confirm:  2,
};

export const MODAL_ACTION = {
	LOGIN:    'login',
	CHECKOUT: 'checkout',
	CONFIRM:  'confirm',
};

export default {
	name:       'PaymentModal',
	components: { Modal, LoginComponent, StepsIndicator, InputText, InputLabel, Button, InputError, Icon, LoadingSpinner, InputCheckbox, PaymentGroupCard, PaymentOfferCard },
	props:      {
		event: {
			type:     Object,
			required: false,
			default:  null,
		},
	},
	data() {
		return {
			steps: [
				this.$I18n.trans('payments.modal.step_1.step'),
				this.$I18n.trans('payments.modal.step_2.step'),
				this.$I18n.trans('payments.modal.step_3.step'),
			],
			activeStep:                  null,
			paymentOffers:               null,
			voucherCode:                 null,
			offer:                       null,
			voucher:                     null,
			error:                       null,
			paymentVerification:         null,
			qrCode:                      null,
			paymentStatus:               null,
			isBusy:                      false,
			hideIntro:                   false,
			voucherChecked:              false,
			voucherValid:                false,
			continueOnOtherDevice:       false,
			confirmSubscription:         false,
			selectedGroupOfferType:      null,
			purchasedOffer:              null,
			selectedGroupOfferConfirmed: false,
			urlParams:                   new URLSearchParams(window.location.search),
			getDateTime,
			PaymentOfferType,
		};
	},
	computed: {
		...mapState({
			profileIsInitialized: state => state.user.isInitialized,
			isLoggedIn:           state => state.isLoggedIn,
		}),
		...mapGetters({
			isLoggedIn:           'user/isLoggedIn',
			profile:              'user/profile',
			subscriptions:        'user/subscriptions',
			fanAccountGeoblocked: 'user/fanAccountGeoblocked',
		}),
		submitText: function() {
			if (this.offer) {
				if (this.voucherValid) {
					return $I18n.trans('payments.modal.step_2.continue_with_voucher');
				}
				return $I18n.trans('payments.modal.step_2.continue', { amount: this.formattedPrice(this.offer) });
			}
			return null;
		},
		loginRedirectUrl: function() {
			return $Helpers.getUrlWithoutParameters() + '#checkout';
		},
		isLogin() {
			return this.activeStep === MODAL_STEPS[MODAL_ACTION.LOGIN];
		},
		isCheckout() {
			return this.activeStep === MODAL_STEPS[MODAL_ACTION.CHECKOUT];
		},
		isConfirm() {
			return this.activeStep === MODAL_STEPS[MODAL_ACTION.CONFIRM];
		},
		isPaymentPending() {
			return this.isConfirm && this.paymentStatus === PAYMENT_STATE.PENDING;
		},
		isPaymentConfirmed() {
			return this.paymentStatus === PAYMENT_STATE.SUCCESS;
		},
		isPaymentFailed() {
			return this.paymentStatus === PAYMENT_STATE.FAILED;
		},
		isPaymentCanceled() {
			return this.paymentStatus === PAYMENT_STATE.CANCELED;
		},
		closeable() {
			return this.mode === 'event' && !(this.isPaymentPending || (this.isConfirm && this.continueOnOtherDevice));
		},
		mode() {
			return this.event ? 'event' : 'subscription';
		},
		profilePage() {
			return laroute.route('arena.fan_account') + '/purchases';
		},
		availableOffers() {
			return this.paymentOffers?.filter((offer) => {
				return !(this.subscriptions || []).includes(offer.id);
			});
		},
		canContinue() {
			return this.offer && !((this.voucherCode && !this.voucherChecked && !this.voucherValid) || (this.offer.type === PaymentOfferType.SUBSCRIPTION && !this.confirmSubscription));
		},
		showGrupedOffers() {
			return this.groupedOffers && this.groupedOffers.length > 0 && !this.selectedGroupOfferConfirmed;
		},
		groupedOffers() {
			if (!this.availableOffers || this.availableOffers.length === 0 || !this.$arenaConfig.paymentOfferGroups) return [];
			const groups = this.$arenaConfig.paymentOfferGroups || [];
			return getGroupedOffers(this.availableOffers, groups);
		},
		filteredAvailableOffers() {
			if (!this.groupedOffers || !this.groupedOffers.length) return this.availableOffers;
			return this.availableOffers.filter(offer => offer.type === this.selectedGroupOfferType);
		},
	},
	watch: {
		profileIsInitialized() {
			if (this.isLoggedIn && this.isLogin) {
				this.activeStep = MODAL_STEPS[MODAL_ACTION.CHECKOUT];
			}
		},
		isLoggedIn() {
			if (this.isLoggedIn && this.isLogin) {
				this.activeStep = MODAL_STEPS[MODAL_ACTION.CHECKOUT];
			}
		},
		fanAccountGeoblocked() {
			if (this.fanAccountGeoblocked) {
				window.eventBus.$emit('modal:GeoblockingModal');
			}
		},
	},
	mounted() {
	},
	methods: {
		formatCurrency,
		onOpen(options) {
			this.getPaymentOffers();
			if (options && options.action) {
				this.activeStep = MODAL_STEPS[options.action];

				if (options.action === MODAL_ACTION.CONFIRM) {
					if (this.urlParams.get('status') === PAYMENT_STATE.SUCCESS) {
						this.paymentStatus = PAYMENT_STATE.PENDING;
						// Wait for final confirmation of payment transaction by webhook
						this.monitorOrderStatus();
					} else {
						this.paymentStatus = this.urlParams.get('status') || PAYMENT_STATE.FAILED;
					}
				}
			}
			if (options && options.qrCode) {
				this.qrCode = options.qrCode;
			}
		},
		reload() {
			window.location.href = $Helpers.getUrlWithoutParameters();
		},
		toProfile() {
			window.location.href = this.profilePage;
		},
		async monitorOrderStatus() {
			this.pending = true;
			const verificationInterval = 6000;
			if (this.paymentVerification) {
				clearInterval(this.tokenVerification);
			}

			this.paymentVerification = setInterval(async() => {
				const response = await authApi.get(
					laroute.route('arena.api.payment.order_code', {
						orderCode: this.urlParams.get('orderCode'),
					})
				);
				const responseContent = await response.json();

				this.handlePaymentResponse(responseContent);
			}, verificationInterval);
		},
		async getPaymentOffers() {
			const params = this.event ? { eventId: this.event.id } : { packageOrSubscription: 1 };
			const response = await httpClient.getPaymentOffers(params);

			this.paymentOffers = Array.isArray(response.data) ? response.data : [];
			this.offer = this.paymentOffers.length > 0 ? this.paymentOffers[0] : null;
		},
		async checkVoucher() {
			this.voucherChecked = false;
			this.voucherValid = false;
			let voucherValid = false;

			try {
				this.isBusy = true;
				const response = await httpClient.getPaymentVoucher(this.voucherCode, this.offer.id);
				voucherValid = response.data && response.data.id;
				this.voucher = response.data;
			} catch (error) {
				console.error(error);
			} finally {
				this.isBusy = false;
			}

			this.voucherChecked = true;
			this.voucherValid = voucherValid;
		},
		loginSubmitted() {
			this.hideIntro = true;
		},
		setOffer(offer) {
			this.offer = offer;
		},
		handlePaymentResponse(response) {
			if (response.status && response.status === ORDER_STATE.CREATED) {
				if (response.redirectUrl) {
					// Redirect to payment gateway
					window.location.href = response.redirectUrl;
				}

				// Order is pending, wait for confirmation
				return;
			}

			if (response.status && response.status === ORDER_STATE.CONFIRMED) {
				this.activeStep = MODAL_STEPS[MODAL_ACTION.CONFIRM];
				this.paymentStatus = PAYMENT_STATE.SUCCESS;
				this.continueOnOtherDevice = response.continueOnOtherDevice;
				this.purchasedOffer = response.offer;

				clearInterval(this.paymentVerification);
			} else {
				console.warn('Error: payment failed for orderCode=' + (this.orderCode || '?') + ', status: ' + response.status);
				this.paymentStatus = PAYMENT_STATE.FAILED;
				this.activeStep = MODAL_STEPS[MODAL_ACTION.CONFIRM];
				clearInterval(this.paymentVerification);
			}
		},
		async submitForm() {
			this.error = null;

			const paymentOrder = {
				offerId:     this.offer.id,
				eventId:     this.event?.id,
				redirectUrl: $Helpers.getUrlWithoutParameters(), // URL without query parameters and hash
				viewerId:    storage.viewerId,
				qrCode:      this.qrCode,
				channel:     $Helpers.isBlueTv() ? 'bluetv' : 'arena',
				domainKey:   window.domainKey,
				locale:      $Helpers.getLocale(),
			};

			try {
				this.isBusy = true;

				if (this.voucher) {
					paymentOrder.voucherId = this.voucher.id;
				};

				const response = await authApi.post(laroute.route('arena.api.payment.order.create'), paymentOrder);
				const responseContent = await response.json();

				if (responseContent && responseContent.id) {
					this.handlePaymentResponse(responseContent);
				} else {
					this.error = true;
					LoggingService.logFatal('Error processing payment', {
						context: {
							paymentOrder,
						},
						scope: 'payment',
					});
				}
			} catch (error) {
				LoggingService.logFatal(error, {
					context: {
						paymentOrder,
					},
					scope: 'payment',
				});
				this.error = true;
			} finally {
				this.isBusy = false;
			}
		},
		formattedPrice(offer) {
			return formatCurrency(offer.currencyCode, offer.amount);
		},
		handleGroupedOfferSelect() {
			this.selectedGroupOfferConfirmed = true;
			this.offer = this.filteredAvailableOffers[0];
		},
		getPaymentOfferName(paymentOffer) {
			if (paymentOffer.type === PaymentOfferType.PPV && this.event && isMatch(this.event)) {
				return this.event.name;
			} else {
				return $Helpers.getTranslatedContent(paymentOffer.name);
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.payment-modal__content {
	max-width: 800px;
	margin-left: auto;
	margin-right: auto;
	width: auto;

	// Reserver space for sticky button
	@include mobile-media-query {
		padding-bottom: 50px;
	}
}

.payment-modal__head {
	text-align: center;
	margin-bottom: 48px;
}

.payment-modal__title {
	@include font(primary, light, normal, fs-150);
	margin-top: 45px;
	margin-bottom: 8px;

	&.-center {
		text-align: center;
	}

	@include mobile-media-query {
		margin-top: 0;
	}
}

.payment-modal__subtitle {
	color: var(--color-grey-300);
	display: inline;

	&.-center {
		text-align: center;
	}
}

.payment-modal__steps {
	margin: 50px 0;

	@include mobile-media-query {
		display: none;
	}
}

.payment-modal__offer {
	background-color: var(--color-grey-900);
	padding: 16px 24px;
	display: flex;

	&.-center {
		text-align: center;
	}
}

.payment-modal__offer-cards-w {
	display: flex;
	flex-direction: column;
	gap: $sp4;
	margin-bottom: $sp6;
}

.voucher__input-container {
	display: flex;
	flex-direction: row;
	gap: $sp4;
}

.voucher__apply {
	display: flex;
	margin-left: auto;
}

.voucher__check {
	margin-top: 16px;
}

.voucher__input {
	flex-grow: 1;

	&::v-deep .v2-input-label.-t-dark {
		color: var(--color-grey-300);
	}
}

.payment-modal__continue {
	margin-top: 48px;
	display: flex;
	width: 100%;
}

.payment-modal__error {
	margin-top: 15px;
}

.payment-modal__icon {
	margin-left: auto;
	margin-right: auto;
	display: flex;
	margin-bottom: 30px;
}

.paywall-modal__loading {
	margin-top: 50px;
	margin-bottom: 50px;
}

.payment-modal__grouped-offers-container {
	width: 100%;
	margin-bottom: $sp7;
	margin-left: auto;
	margin-right: auto;

	@include size(md-up) {
		width: fit-content;
	}
}

.payment-modal__grouped-offers {
	display: flex;
	flex-direction: row;
	justify-content: center;
	margin-bottom: $sp7;
	gap: $sp4;
	overflow-x: auto;
}

.payment-modal__grouped-offers-button {
	width: 100%;
}
</style>
